import type { SVGProps } from 'react';

import { cn } from '@parrot-software/utils';

/**
 * Brand red of the design-system logo (Figma `2060:27`, symbol `parrot-logo`).
 * Kept local to this file: it is the only place in the repo allowed to hardcode it.
 */
const BRAND_RED = '#DF4F4C';

export type ParrotLogoProps = SVGProps<SVGSVGElement> & {
  /**
   * `color` (default) paints the brand red. `mono` paints `currentColor`, which is how the logo
   * goes white over the red panel of the activation layout: the surface sets the color with
   * a text utility (`text-primary-foreground`) and the art follows.
   */
  variant?: 'color' | 'mono';
};

/**
 * Parrot logotype — the `parrot®` wordmark from the design system (Figma `2060:27`, 85×48).
 *
 * Brand red is the colorway of the logo on any light or dark surface. The one exception is a
 * surface that is already brand red — the split panel of the activation layout — where the art has to invert:
 * `variant="mono"` paints `currentColor` so the container decides with a text utility.
 *
 * The SVG is inlined (never `<img src>`) so the fill travels with the component instead of
 * depending on an asset. It carries no `width`/`height`: size it by height alone (`h-16`) and the
 * art keeps its own ratio. Never set the width to `fit-content`: Safari resolves it to the width of
 * the container, and the art then floats to the middle of that box instead of sitting at its edge.
 *
 * Accessibility: renders `role="img"` plus a `<title>`. When the logo sits next to text that
 * already names the brand, pass `aria-hidden` so screen readers do not announce it twice.
 *
 * @param {ParrotLogoProps} props - Component properties
 * @example
 * <ParrotLogo className="h-16" />
 * <ParrotLogo className="h-10 text-primary-foreground" variant="mono" />
 */
export const ParrotLogo = ({ className, variant = 'color', ...props }: ParrotLogoProps) => {
  const fill = variant === 'mono' ? 'currentColor' : BRAND_RED;

  return (
    <svg
      className={cn('aspect-[85/48] w-auto', className)}
      fill="none"
      preserveAspectRatio="xMinYMid"
      role="img"
      viewBox="0 0 85 48"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <title>Parrot</title>
      <path
        d="M67.9438 25.204V19.8449H69.816V16.6253H67.9438V11.3609H65.0812V16.6253H63.2378V19.8449H65.0812V25.2045C65.0812 28.1698 67.2164 30.5845 69.8408 30.5845V27.3485C68.7942 27.3485 67.9438 26.3867 67.9438 25.204Z"
        fill={fill}
      />
      <path
        d="M46.2976 16.9827H43.5798V30.5275H46.2976V23.2338C46.4211 22.1724 46.7478 21.2773 47.4207 20.5484C47.8453 20.088 48.3973 19.7738 48.8918 19.604V16.5302C48.0272 16.6973 47.1626 17.0898 46.2976 17.7084V16.9827Z"
        fill={fill}
      />
      <path
        d="M38.7835 16.9827H36.0657V30.5275H38.7835V23.2338C38.907 22.1724 39.2337 21.2773 39.9066 20.5484C40.3312 20.088 40.8832 19.7738 41.3777 19.604V16.5302C40.5131 16.6973 39.6485 17.0898 38.7835 17.7084V16.9827Z"
        fill={fill}
      />
      <path
        d="M56.3488 17.08C52.8111 17.08 49.9331 20.1085 49.9331 23.8298C49.9331 27.5511 52.8111 30.5796 56.3488 30.5796C58.2768 30.5796 60.0848 29.68 61.3089 28.1116C62.2479 26.9089 62.7645 25.3885 62.7645 23.8298C62.7645 22.2711 62.3028 20.8978 61.4289 19.7071C60.2035 18.0378 58.3516 17.08 56.3488 17.08ZM59.1835 26.2751C58.4835 27.172 57.4503 27.6862 56.3488 27.6862C54.3278 27.6862 52.6836 25.9565 52.6836 23.8298C52.6836 21.7031 54.3278 19.9734 56.3488 19.9734C57.4932 19.9734 58.5517 20.5209 59.2525 21.4756C59.751 22.1547 60.0144 22.9689 60.0144 23.8298C60.0144 24.7333 59.7271 25.5791 59.1835 26.2751Z"
        fill={fill}
      />
      <path
        d="M30.6642 18.3005C29.6004 17.5129 28.3258 17.0805 26.9822 17.0805C23.4445 17.0805 20.5665 20.1089 20.5665 23.8302C20.5665 27.5516 23.4445 30.58 26.9822 30.58C28.3187 30.58 29.5973 30.1467 30.6642 29.3587V30.468H33.4147V17.08H30.6642V18.3005ZM29.8169 26.2751C29.1169 27.172 28.0841 27.6862 26.9822 27.6862C24.9612 27.6862 23.3166 25.9565 23.3166 23.8298C23.3166 21.7031 24.9608 19.9734 26.9822 19.9734C28.1266 19.9734 29.1851 20.5209 29.8859 21.4756C30.3848 22.1547 30.6478 22.9689 30.6478 23.8298C30.6478 24.7334 30.3605 25.5791 29.8169 26.2751Z"
        fill={fill}
      />
      <path
        d="M13.2414 17.08C11.8982 17.08 10.6232 17.5129 9.5594 18.3V17.08H6.80841V36.6391H9.55852V29.3587C10.625 30.1471 11.904 30.58 13.2405 30.58C16.7782 30.58 19.6562 27.5525 19.6562 23.8302C19.6562 20.108 16.7782 17.08 13.241 17.08H13.2414ZM13.2414 27.6862C12.1399 27.6862 11.1067 27.1716 10.4067 26.2756C9.86354 25.5791 9.57578 24.7338 9.57578 23.8307C9.57578 22.9276 9.83919 22.1556 10.3377 21.4765C11.038 20.5218 12.0966 19.9742 13.2414 19.9742C15.2624 19.9742 16.9066 21.704 16.9066 23.8307C16.9061 25.9569 15.2619 27.6862 13.2414 27.6862Z"
        fill={fill}
      />
      <path
        d="M76.3729 14.292C76.5527 14.1298 76.6426 13.9133 76.6426 13.6431C76.6426 13.3547 76.5527 13.1294 76.3729 12.9671C76.1932 12.8049 75.9506 12.7236 75.6456 12.7236H74.2719V15.6169H75.0395V14.5351H75.2414L75.8745 15.6169H76.7767L76.0405 14.4787C76.168 14.4382 76.2791 14.376 76.3725 14.2916L76.3729 14.292ZM75.0399 13.3996H75.5384C75.6372 13.3996 75.7177 13.42 75.781 13.4605C75.8439 13.5009 75.8753 13.5711 75.8753 13.6702C75.8753 13.7782 75.8439 13.8551 75.781 13.9C75.7182 13.9453 75.6372 13.9676 75.5384 13.9676H75.0399V13.3996Z"
        fill={fill}
      />
      <path
        d="M77.8343 12.7436C77.5961 12.3244 77.2685 11.9933 76.8511 11.7498C76.4336 11.5062 75.9555 11.3849 75.4167 11.3849C74.8779 11.3849 74.3843 11.5067 73.962 11.7498C73.5396 11.9933 73.2098 12.3244 72.9721 12.7436C72.7339 13.1627 72.6152 13.6476 72.6152 14.1969C72.6152 14.7462 72.7343 15.2222 72.9721 15.6502C73.2098 16.0787 73.5401 16.412 73.962 16.6507C74.3839 16.8898 74.8686 17.0089 75.4167 17.0089C75.9648 17.0089 76.4336 16.8893 76.8511 16.6507C77.2685 16.412 77.5961 16.0782 77.8343 15.6502C78.0721 15.2222 78.1911 14.7378 78.1911 14.1969C78.1911 13.656 78.0721 13.1627 77.8343 12.7436ZM77.2889 15.3467C77.1092 15.6804 76.8599 15.9418 76.5416 16.1307C76.2229 16.32 75.8479 16.4147 75.4171 16.4147C74.9864 16.4147 74.5955 16.32 74.2723 16.1307C73.9491 15.9413 73.6977 15.68 73.5179 15.3467C73.3382 15.0133 73.2483 14.6302 73.2483 14.1973C73.2483 13.7644 73.3382 13.384 73.5179 13.0547C73.6972 12.7258 73.9491 12.4667 74.2723 12.2773C74.5955 12.088 74.9771 11.9933 75.4171 11.9933C75.8572 11.9933 76.2229 12.088 76.5416 12.2773C76.8604 12.4667 77.1096 12.7258 77.2889 13.0547C77.4686 13.384 77.5581 13.7644 77.5581 14.1973C77.5581 14.6302 77.4682 15.0133 77.2889 15.3467Z"
        fill={fill}
      />
    </svg>
  );
};

/**
 * Parrot isotype — the bird mark alone, for surfaces too narrow for the wordmark
 * (the mobile app bar). Same two colorways as {@link ParrotLogo}.
 *
 * The design system has no isotype symbol (`2060:27` ships the wordmark only), so the art here is
 * the repo's existing mark, recolored to the design-system red so both exports agree. The art is
 * portrait (161×285): size it by height and let the width follow.
 *
 * @param {ParrotLogoProps} props - Component properties
 * @example
 * <ParrotLogoMark className="h-9" />
 */
export const ParrotLogoMark = ({ className, variant = 'color', ...props }: ParrotLogoProps) => {
  const fill = variant === 'mono' ? 'currentColor' : BRAND_RED;

  return (
    <svg
      className={cn('aspect-[161/285] w-auto', className)}
      fill="none"
      preserveAspectRatio="xMinYMid"
      role="img"
      viewBox="0 0 161 285"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <title>Parrot</title>
      <path
        d="M129.231 268.358C148.97 253.118 160.276 230.1 160.276 205.182C160.276 161.18 124.482 125.409 80.5032 125.409C80.3902 125.409 80.2771 125.409 80.1641 125.409C69.6046 125.341 59.746 121.791 51.6059 115.098C41.0012 106.393 34.9187 93.5496 34.9187 79.8471C34.9187 54.7033 55.3594 34.2626 80.5032 34.2626C105.647 34.2626 126.088 54.7033 126.088 79.8471C126.088 85.8165 124.957 91.6276 122.719 97.0996C122.334 98.0266 121.927 98.9537 121.498 99.8356L152.226 114.872C152.995 113.289 153.741 111.661 154.397 110.033C158.332 100.446 160.321 90.271 160.321 79.8471C160.321 35.8454 124.528 0.0742188 80.5484 0.0742188C36.5467 0.0742188 0.775536 35.868 0.775536 79.8471C0.775536 103.815 11.4029 126.291 29.9442 141.531C30.3738 141.87 30.826 142.187 31.2557 142.526C12.7143 157.155 0.75293 179.789 0.75293 205.205C0.75293 229.173 11.3803 251.648 29.9216 266.888C44.1441 278.556 62.1202 285 80.5258 285C98.321 284.955 115.166 279.212 129.231 268.358ZM51.6059 240.433C41.0012 231.728 34.9187 218.884 34.9187 205.182C34.9187 180.129 55.2463 159.733 80.2545 159.62C80.3223 159.62 80.4128 159.62 80.4806 159.62V159.597C105.624 159.597 126.065 180.038 126.065 205.182C126.065 219.404 119.598 232.564 108.315 241.292C100.288 247.488 90.6783 250.766 80.4806 250.766C69.8307 250.744 59.8364 247.194 51.6059 240.433Z"
        fill={fill}
      />
      <path
        d="M95.8111 79.802C95.8111 88.2587 88.9599 95.1099 80.5032 95.1099C72.0466 95.1099 65.1953 88.2587 65.1953 79.802C65.1953 71.3454 72.0466 64.4941 80.5032 64.4941C88.9599 64.5168 95.8111 71.368 95.8111 79.802Z"
        fill={fill}
      />
    </svg>
  );
};
